import { MoveRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../common/Button";
import { friendlyStatus } from "../../lib/api";
import { formatDate } from "../../lib/datetime";
import { toast } from "../../stores/toast";
import styles from "./NodeUnreachableBanner.module.css";

/**
 * This AgntSpace is PARKED: its vault moved to a node (remote-node program
 * N2 half 2, 2026-09-08). Channels, routines and watchers are off here so
 * two machines never act on two copies. The banner says where the vault
 * went, opens the node, and offers Roll back — the vault here was never
 * deleted (Rule 29: the outcome and its options, never a dead end).
 */
interface Health { parked?: boolean; moved_to?: string; moved_at?: string }

async function readDetail(res: Response): Promise<string> {
	const text = await res.text().catch(() => "");
	try {
		const body = JSON.parse(text);
		if (typeof body?.detail === "string") return body.detail;
	} catch { /* not json */ }
	return text.trim() || friendlyStatus(res.status);
}

export function NodeMovedBanner() {
	const { t } = useTranslation();
	const [health, setHealth] = useState<Health | null>(null);
	const [busy, setBusy] = useState(false);
	const [rolledBack, setRolledBack] = useState<string | null>(null);
	const [restarting, setRestarting] = useState(false);

	useEffect(() => {
		let alive = true;
		fetch("/api/health").then((r) => (r.ok ? r.json() : null)).then((h) => { if (alive && h) setHealth(h as Health); }).catch(() => {});
		return () => { alive = false; };
	}, []);

	if (!health?.parked) return null;

	const restart = async () => {
		setRestarting(true);
		try {
			const res = await fetch("/api/node/restart", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reason: "after rollback" }) });
			if (!res.ok) throw new Error(await readDetail(res));
			let wentDown = false;
			for (let i = 0; i < 120; i += 1) {
				await new Promise((r) => setTimeout(r, 2000));
				try {
					const h = await fetch("/api/health", { cache: "no-store" });
					if (h.ok && wentDown) { window.location.reload(); return; }
				} catch { wentDown = true; }
			}
		} catch (e) {
			toast.error(t("nav.movedRestartFailed", "Could not restart"), String((e as Error).message ?? e));
			setRestarting(false);
		}
	};

	const rollback = async () => {
		setBusy(true);
		try {
			const res = await fetch("/api/node/move/rollback", { method: "POST" });
			if (!res.ok) throw new Error(await readDetail(res));
			const body = (await res.json()) as { note?: string };
			setRolledBack(body.note || t("nav.movedRolledBack", "Rolled back. Restart this AgntSpace to resume here."));
			toast.success(t("nav.movedRolledBackTitle", "This AgntSpace owns its vault again at the next start"), body.note || "");
		} catch (e) {
			toast.error(t("nav.movedRollbackFailed", "Could not roll back"), String((e as Error).message ?? e));
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className={styles.banner} role="status" aria-live="polite" data-node-moved>
			<MoveRight size={16} aria-hidden className={styles.icon} />
			<div className={styles.body}>
				<div className={styles.title}>{t("nav.movedTitle", "This vault moved to {{node}}", { node: health.moved_to || "another machine" })}</div>
				<div className={styles.cause}>
					{health.moved_at ? t("nav.movedOn", "Moved {{when}}. ", { when: formatDate(health.moved_at) }) : ""}
					{t("nav.movedPaused", "AgntSpace here is paused — no channels, routines or watchers — so two machines never act on two copies. The vault on this computer is kept as it was at the move.")}
				</div>
				{rolledBack ? (
					<div className={styles.cause} data-node-moved-rolled-back>{rolledBack} <Button size="sm" variant="primary" loading={restarting} onClick={() => void restart()}>{t("nav.movedRestartNow", "Restart now")}</Button></div>
				) : (
					<ul className={styles.options}>
						<li>{t("nav.movedOptOpen", "Use it where it lives now: open the node (pair this device there once).")}</li>
						<li>{t("nav.movedOptRollback", "Changed your mind? Roll back makes this computer the owner again at its next start; changes made on the node since the move stay there — stop AgntSpace on the node first.")}</li>
					</ul>
				)}
			</div>
			<div style={{ display: "flex", gap: "var(--space-sm)", flexShrink: 0 }}>
				{health.moved_to && (
					<a href={health.moved_to} target="_blank" rel="noreferrer"><Button size="sm" variant="primary">{t("nav.movedOpen", "Open the node")}</Button></a>
				)}
				{!rolledBack && <Button size="sm" variant="secondary" loading={busy} onClick={() => void rollback()}>{t("nav.movedRollback", "Roll back")}</Button>}
			</div>
		</div>
	);
}
